import * as yup from 'yup'
import { ChangeEvent, CSSProperties, useEffect, useState } from 'react'
import { IOptionsProps } from '../../../../../@types/reactSelect/IOption'
import { useEditOnboarding } from '../../../../../hooks/useEditOnboarding'
import { userService } from '../../../../../services/userService'
import { fetchStateCities, fetchStatesUF } from '../../../../../services/ibge'
import { sortBy } from '../../../../../utils/arrays/sortBy'
import { FooterEditOnboarding } from '../../../../EditOnboarding/Footer'
import '../../../styles.css'
import './styles.css'
import { SliderComponent } from '../../../../EditOnboarding/Components/Slider/Index'
import Constants from '../../../../../constants/Index'
import { cpf, cnpj } from 'cpf-cnpj-validator'
import { ButtonRedirect } from '../../../../Modal/Error/ButtonRedirect/Index'
import { AlertModal } from '../../../../Modal/Alert/Index'
import { TitleModal } from '../../../../Modal/Contents/TitleModal/Index'
import { TextModal } from '../../../../Modal/Contents/TextModal/Index'
import { SimpleButtonClose } from '../../../../Modal/Contents/SimpleButtonClose/Index'
import axios from '../../../../../services/axios'
import { FarmFormV2, FarmFormValueV2, FieldErrorsV2 } from '../../../../FormsV2'
import { DrawOnMapSlotV2 } from '../../../../OnboardingV2/DrawOnMapSlotV2'
import { ICoords } from '../../../../../@types/GoogleMaps/ICoord'
import { readFileKML } from '../../../../../utils/kml/readFile'

function buildFarmFormValue(defaultValue: any, managers: IOptionsProps): FarmFormValueV2 {
  return {
    id: defaultValue?.id,
    name: defaultValue?.name || '',
    initials: defaultValue?.initials || '',
    document: defaultValue?.document || '',
    state_registration: defaultValue?.state_registration || '',
    manager_user_id:
      managers?.find((manager) => String(manager.value) === String(defaultValue?.owner_user_id)) ||
      (defaultValue?.owner_user_id
        ? {
          label: defaultValue?.owner_user_name || String(defaultValue.owner_user_id),
          value: defaultValue.owner_user_id,
        }
        : null),
    address: {
      state: defaultValue?.address?.state
        ? {
          label: defaultValue.address.state,
          value: defaultValue.address.state,
        }
        : null,
      city: defaultValue?.address?.city?.code
        ? {
          label: defaultValue.address.city.name,
          value: defaultValue.address.city.code,
        }
        : null,
    },
    season: {
      rain_initial: Number(defaultValue?.season?.rain_initial || 3),
      dry_initial: Number(defaultValue?.season?.dry_initial || 8),
    },
    map_coords: defaultValue?.map_coords || [],
  }
}

function normalizeCoords(coords: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords
  if (Array.isArray(coords?.coords)) return coords.coords
  return []
}

function toFieldErrors(error: yup.ValidationError) {
  return error.inner.reduce((errors, currentError) => {
    if (currentError.path) errors[currentError.path] = currentError.message
    return errors
  }, {} as FieldErrorsV2)
}

export function CreateFarm() {
  const { setOptionForm, defaultValue } = useEditOnboarding()
  const [submiting, setSubmiting] = useState(false)
  const isNew = !defaultValue
  const [listUF, setListUF] = useState<IOptionsProps>([])
  const [managers, setManagers] = useState<IOptionsProps>([])
  const [listCities, setListCities] = useState<IOptionsProps>([])
  const [areasList, setAreasList] = useState<any[]>()
  const [msgError, setMsgError] = useState<string>('')
  const [farmV2, setFarmV2] = useState<FarmFormValueV2>(() => buildFarmFormValue(defaultValue, managers))
  const [errorsV2, setErrorsV2] = useState<FieldErrorsV2>({})
  const [kmlError, setKmlError] = useState('')
  const [kmlFileName, setKmlFileName] = useState('')

  const farmSchemaV2 = yup.object().shape({
    name: yup.string().required('Campo obrigatório não informado.'),
    initials: yup
      .string()
      .matches(/^\S+(\s+\S+)*$/, 'Esta sigla não é válida.')
      .required('Sigla é necessário.'),
    document: yup
      .string()
      .notRequired()
      .test('documentCPF', 'CPF informado inválido.', (value) => {
        const valueParsed = value?.toString().replaceAll('.', '').replaceAll('-', '')
        if (!!valueParsed && valueParsed.length < 12) return cpf.isValid(valueParsed)
        return true
      })
      .test('documentCNPJ', 'CNPJ informado inválido.', (value) => {
        const valueParsed = value?.toString().replaceAll('.', '').replaceAll('-', '')
        if (!!valueParsed && valueParsed.length > 11 && valueParsed.length < 15) return cnpj.isValid(value || '')
        return true
      }),
    manager_user_id: yup.object().nullable().required('Campo obrigatório não informado.'),
    address: yup.object({
      state: yup.object().nullable().required('UF é necessário.'),
      city: yup.object().nullable().required('Cidade é necessário.'),
    }),
    map_coords: yup.array().nullable().min(1, 'Mapa da fazenda é necessário.'),
  })

  const contentStyleAlert = {
    width: '335px',
    height: '237px',
    borderRadius: '20px',
    padding: '20px',
  } as CSSProperties

  useEffect(() => {
    if (!isNew && defaultValue?.id)
      axios
        .get('/areas/', { params: { farm_id: defaultValue.id } })
        .then(({ data }) => setAreasList(data));
  }, [defaultValue]);

  const deleteFarm = () => {
    if (defaultValue?.id) {
      axios
        .delete(`/farms/${defaultValue.id}`)
        .then(() => {
          setOptionForm(undefined);
        })
        .catch((err) => setMsgError(err.response.data.msg));
    }
  }

  async function handleStateChangeV2(state: any) {
    setFarmV2((current) => ({ ...current, address: { ...current.address, state, city: null } }))
    if (!state) {
      setListCities([])
      return
    }

    const data = await fetchStateCities(String(state.value))
    setListCities(
      data
        .map((city: any) => ({ label: city.nome, value: city.id }))
        .sort(sortBy('label'))
    )
  }

  async function handleFarmKmlChange(event: ChangeEvent<HTMLInputElement>) {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    if (!selectedFile.name.toLowerCase().endsWith('.kml')) {
      setKmlError('Selecione um arquivo KML válido.')
      event.target.value = ''
      return
    }

    try {
      const response = (await readFileKML(event)) as { coords: ICoords }[] | null
      const coords = response?.[0]?.coords || []

      if (coords.length < 3) {
        setKmlError(`Não foi possível ler coordenadas válidas do arquivo ${selectedFile.name}.`)
        event.target.value = ''
        return
      }

      setFarmV2((current) => ({ ...current, map_coords: coords }))
      setKmlFileName(selectedFile.name)
      setKmlError('')
    } catch {
      setKmlError(`Não foi possível ler coordenadas válidas do arquivo ${selectedFile.name}.`)
    } finally {
      event.target.value = ''
    }
  }

  const submitFarmV2 = async () => {
    if (submiting) return

    try {
      await farmSchemaV2.validate(farmV2, { abortEarly: false })
      setErrorsV2({})
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrorsV2(toFieldErrors(error))
      return
    }

    setSubmiting(true)

    if (isNew) {
      const formCreateFarm = [
        {
          name: farmV2.name,
          initials: farmV2.initials,
          document: farmV2.document ? farmV2.document.replace(/[^0-9a-z]/gi, '') : undefined,
          address: {
            city: {
              code: farmV2.address.city?.value,
              name: farmV2.address.city?.label,
            },
            state: farmV2.address.state?.label,
          },
          properties: {
            season: {
              dry_initial: farmV2.season.dry_initial,
              rain_initial: farmV2.season.rain_initial,
            },
          },
          state_registration: farmV2.state_registration,
          owner_user_id: farmV2.manager_user_id?.value,
          map_coords: farmV2.map_coords,
        },
      ]

      axios
        .post('/farms', formCreateFarm)
        .then(() => {
          setOptionForm(undefined)
        })
        .catch((err) => setMsgError(err.response.data.msg))
        .finally(() => {
          setSubmiting(false)
        })
    } else {
      const formPatchFarm = [
        {
          farm_id: defaultValue?.id,
          state_registration: farmV2.state_registration,
          name: farmV2.name,
          document: farmV2.document,
          owner_user_id: farmV2.manager_user_id?.value,
          properties: {
            season: {
              dry_initial: farmV2.season.dry_initial,
              rain_initial: farmV2.season.rain_initial,
            },
          },
        },
      ]

      axios
        .patch('/farms', formPatchFarm)
        .then(() => {
          setOptionForm(undefined)
        })
        .catch((err) => setMsgError(err.response.data.msg))
        .finally(() => {
          setSubmiting(false)
        })
    }
  }

  useEffect(() => {
    fetchStatesUF().then((data) => {
      const ufs: Array<any> = data.map((uf: any) => ({
        label: uf,
        value: uf,
      }));
      setListUF(() => ufs.sort(sortBy("label")));
    });

    userService
      .getUsersByOffice(Constants.PROFILE_CODE_GERENTE.toString())
      .then((data) => {
        setManagers(
          data.map((user: any) => ({
            value: user.id,
            label: user.name,
          }))
        )
      })
  }, []);

  useEffect(() => {
    if (!isNew && defaultValue) {
      setFarmV2(buildFarmFormValue(defaultValue, managers))
    }
  }, [defaultValue, isNew, managers])

  const farmMapCoords = normalizeCoords(farmV2.map_coords)

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError('')} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>Algo deu errado.</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError('')}>
            Voltar
          </ButtonRedirect>
        </section>
      </AlertModal>
      <main className="body__formEditOnboarding body__formEditOnboarding--farm-v2">
        <FarmFormV2
          value={farmV2}
          mode={isNew ? 'create' : 'edit'}
          managers={managers || []}
          states={listUF || []}
          cities={listCities || (farmV2.address.city ? [farmV2.address.city] : [])}
          autoGenerateInitials={isNew}
          errors={errorsV2}
          disabledFields={isNew ? [] : ['initials', 'address.state', 'address.city']}
          showActions={false}
          showCardHeader={false}
          cardTitle={isNew ? 'Nova Fazenda' : 'Fazenda'}
          cardDescription={
            farmV2.name ? `${farmV2.name}${farmV2.initials ? ` (${farmV2.initials})` : ''}` : undefined
          }
          onStateChange={handleStateChangeV2}
          seasonSlot={
            <div className="ov2-property-form__slider">
              <SliderComponent
                listOptions={Constants.MONTHS_SHORT_NAME}
                rain_initial={farmV2.season.rain_initial}
                dry_initial={farmV2.season.dry_initial}
                setRainInitial={(rain_initial) =>
                  setFarmV2((current) =>
                    current.season.rain_initial === rain_initial
                      ? current
                      : {
                        ...current,
                        season: { ...current.season, rain_initial },
                      }
                  )
                }
                setDryInitial={(dry_initial) =>
                  setFarmV2((current) =>
                    current.season.dry_initial === dry_initial
                      ? current
                      : {
                        ...current,
                        season: { ...current.season, dry_initial },
                      }
                  )
                }
              />
            </div>
          }
          mapSlot={
            <div className="edit-farm-v2__map-slot">
              {farmMapCoords.length > 0 ? (
                <>
                  {isNew && (
                    <div className="edit-farm-v2__map-toolbar">
                      <span>{kmlFileName || 'Mapa da fazenda carregado'}</span>
                      <label className="ov2-kml-upload__button" htmlFor="edit-farm-v2-kml">
                        Trocar KML
                      </label>
                      <input
                        id="edit-farm-v2-kml"
                        className="ov2-kml-upload__input"
                        type="file"
                        accept=".kml,application/vnd.google-earth.kml+xml"
                        onChange={handleFarmKmlChange}
                      />
                    </div>
                  )}
                  <DrawOnMapSlotV2
                    value={farmMapCoords}
                    staticsElements={(areasList && areasList.map((area: any) => normalizeCoords(area.map_coords))) || []}
                    initialsLabel={farmV2.initials || ''}
                    editable={isNew}
                    style={{ width: '100%', height: '240px' }}
                    onChange={(map_coords) => setFarmV2((current) => ({ ...current, map_coords }))}
                  />
                </>
              ) : (
                <div className="ov2-kml-upload">
                  <span className="ov2-kml-upload__eyebrow">Mapa da fazenda</span>
                  <strong>Upload Mapa KML</strong>
                  <p>Selecione um arquivo KML com o polígono da fazenda para carregar o mapa.</p>
                  <label className="ov2-kml-upload__button" htmlFor="edit-farm-v2-kml">
                    Selecionar KML
                  </label>
                  <input
                    id="edit-farm-v2-kml"
                    className="ov2-kml-upload__input"
                    type="file"
                    accept=".kml,application/vnd.google-earth.kml+xml"
                    onChange={handleFarmKmlChange}
                  />
                  {(kmlError || errorsV2.map_coords) && (
                    <small className="ov2-field__error animation__fadeIn">
                      {kmlError || errorsV2.map_coords}
                    </small>
                  )}
                </div>
              )}
            </div>
          }
          onChange={setFarmV2}
        />
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitFarmV2}
        deleteFunction={isNew ? undefined : deleteFarm}
        isNew={isNew}
        disabledSubmit={submiting}
      />
    </>
  );
}
